import React, { useState, useContext, useEffect } from "react";
import {
  Outlet,
  Link,
  useNavigate,
  Navigate,
  useLocation,
} from "react-router-dom";

import "../../styles/mainlayout.css";
import ThemeToggle from "../themetoggle";
import Logo from "../../assets/logo.png";
import { ShareContext } from "../../context/ShareContext";
import DynamicIcon from "../dynamicicon";
import JisAlert from "../jisalert";
import JisPrompt from "../jisprompt";
import { X } from "lucide-react";
import TawkToChat from "../tawkto";

function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(-1);
  const {
    profile,
    setToken,
    sidelinks,
    activePage,
    token,
    checkingToken,
    setActivePage,
    isExpired,
    setIsExpired,
    alert,
    setAlert,
  } = useContext(ShareContext);
  const [search, setSearch] = useState("");
  const [openMenus, setOpenMenus] = useState({});
  const [isDraft, setIsDraft] = useState(false);
  console.log(profile);
  const [isSideBarOpen, setIsSideBarOpen] = useState(true);

  const handleSidebarToggle = () => {
    setIsSideBarOpen(!isSideBarOpen);
  };

  const handleToggle = (id) => {
    setOpenMenus((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleParentClick = (item) => {
    // Toggle the menu open/closed
    const isCurrentlyOpen = openMenus[item.id];
    handleToggle(item.id);

    // If opening the menu and it has children, navigate to the first child
    if (!isCurrentlyOpen && item.inner && item.inner.length > 0) {
      const firstChild = item.inner[0];
      setActivePage(firstChild);
      navigate(firstChild.url);
    }
  };

  const handleMenuItemClick = (item) => {
    // Close all open menus when clicking a standalone menu item
    setOpenMenus({});

    // Navigate to the item
    setActivePage(item);
    navigate(item.url);
  };

  const handleChildClick = (item, parentId) => {
    // Keep only the current parent menu open, close others
    setOpenMenus({ [parentId]: true });

    // Navigate to the child item
    setActivePage(item);
    navigate(item.url);
  };

  // Check if a parent menu item should be active (has an active child)
  const isParentActive = (item) => {
    // Parent is active if any child is currently active
    return hasActiveChild(item);
  };

  // Check if any child of this parent is active
  const hasActiveChild = (item) => {
    if (item.inner && item.inner.length > 0) {
      return item.inner.some((subItem) => location.pathname === subItem.url);
    }
    return false;
  };

  // Check if a child menu item is active
  const isChildActive = (subItem) => {
    return location.pathname === subItem.url;
  };

  const onLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("profile");
    setToken(null);
    setIsExpired(false);
    navigate("/login");
  };

  useEffect(() => {
    var cc = localStorage.getItem("caseinfo");

    if (cc != null && profile != null) {
      // ← Add profile check
      var _sf = JSON.parse(cc);
      if (_sf.userid == profile?.id) {
        setIsDraft(true);
      }
    }
  }, [profile]); // ← Add profile as dependency

  // Update active page based on current pathname
  useEffect(() => {
    const findActiveItem = () => {
      for (const item of sidelinks) {
        // Check parent item
        if (item.url === location.pathname) {
          setActivePage(item);
          return;
        }

        // Check children items
        if (item.inner && item.inner.length > 0) {
          for (const subItem of item.inner) {
            if (subItem.url === location.pathname) {
              setActivePage(subItem);
              // Auto-open parent menu
              setOpenMenus((prev) => ({
                ...prev,
                [item.id]: true,
              }));
              return;
            }
          }
        }
      }
    };

    setIsSideBarOpen(false);
    findActiveItem();
  }, [location.pathname, sidelinks]);

  const onDraftOk = () => {
    setIsDraft(false);
    navigate("/case-filing");
  };

  const onDraftCancel = () => {
    setIsDraft(false);
    localStorage.removeItem("caseinfo");
  };

  const onCloseAlert = () => {
    const callback = alert.onOk;
    setAlert({ open: false });
    if (callback) callback();
  };

  const onChangeProfile = () => {
    navigate("/profile");
  };

  if (checkingToken)
    return (
      <div
        style={{
          background: "var(--primary)",
          padding: 16,
          color: "var(--background)",
        }}
      >
        <div className="spinner"></div> JIS System reconnecting
      </div>
    );
  return token ? (
    <div className="layout-wrapper">
      {/* Side Bar */}
      <div className={`sidebar ${isSideBarOpen ? "side_bar_open" : ""} `}>
        <div className="sidebar-toggle-wrapper">
          <button className="sidebar-toggle" onClick={handleSidebarToggle}>
            <X size={24} />
          </button>
        </div>
        <div
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <img src={Logo} alt="JIS Logo" className="sidebar_logo" />
        </div>

        <div
          className="no-print"
          style={{ display: "flex", flexDirection: "column", flex: 1 }}
        >
          <nav className="sidebar-links">
            <ul>
              {sidelinks.map((item) => (
                <li key={item.id}>
                  {/* Parent with children */}
                  {item.inner && item.inner.length > 0 ? (
                    <>
                      <div
                        className={`parent-menu-item ${
                          isParentActive(item) ? "active" : ""
                        }`}
                        onClick={() => handleParentClick(item)}
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        <DynamicIcon iname={item.icon} />
                        <span style={{ flex: 1 }}>{item.title}</span>
                        <span>
                          {openMenus[item.id] ? (
                            <DynamicIcon iname="chevron-down" />
                          ) : (
                            <DynamicIcon iname="chevron-right" />
                          )}
                        </span>
                      </div>

                      {/* Children submenu */}
                      <ul
                        className={`submenu ${
                          openMenus[item.id] ? "open" : ""
                        }`}
                      >
                        {item.inner.map((subItem) => (
                          <li key={subItem.id}>
                            <Link
                              className={`${
                                isChildActive(subItem) ? "active" : ""
                              }`}
                              onClick={(e) => {
                                e.preventDefault();
                                handleChildClick(subItem, item.id);
                              }}
                              to={subItem.url}
                            >
                              <span>{subItem.title}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    // Standalone link
                    <Link
                      className={`${
                        location.pathname === item.url ? "active" : ""
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleMenuItemClick(item);
                      }}
                      to={item.url}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <DynamicIcon iname={item.icon} />
                      <span>{item.title}</span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <div
              className="special-menu-link"
              onClick={onLogout}
              style={{
                color: "black",
              }}
            >
              <DynamicIcon iname="headset" size={20} />
              <span style={{ margin: 1 }}>Help</span>
            </div>
            <div
              className="special-menu-link"
              onClick={onLogout}
              style={{
                color: "red",
              }}
            >
              <DynamicIcon iname="log-out" size={20} />
              <span style={{ margin: 1 }}>Logout</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Bar */}
        <div className="top-bar no-print">
          <div className="top-bar-left">
            <button className="sidebar-toggle" onClick={handleSidebarToggle}>
              <DynamicIcon iname="menu" size={24} />
            </button>
            {profile && profile.firstName ? (
              <div>
                <div className="top-bar-username">
                  {profile.suffix && `${profile.suffix} `}
                  {profile.firstName.charAt(0).toUpperCase() +
                    profile.firstName.slice(1).toLowerCase()}
                  {profile.lastName &&
                    ` ${
                      profile.lastName.charAt(0).toUpperCase() +
                      profile.lastName.slice(1).toLowerCase()
                    }`}
                </div>
                <div className="top-bar-user-role">
                  {profile?.role?.name || "User"}
                </div>
              </div>
            ) : null}
          </div>
          <div className="top-bar-right">
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "#F0F2F5",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "50%",
                padding: "10px",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.834 2.5013C10.834 2.04106 10.4609 1.66797 10.0006 1.66797C9.54038 1.66797 9.16728 2.04106 9.16728 2.5013V2.97703C6.34052 3.38124 4.16729 5.8115 4.16729 8.75039L4.16728 12.0841C4.16728 12.0841 4.16729 12.0839 4.16728 12.0841C4.1672 12.0857 4.16664 12.0968 4.16302 12.1187C4.15869 12.1449 4.15098 12.1806 4.13827 12.2265C4.1125 12.3196 4.07148 12.4355 4.01418 12.5725C3.89929 12.8473 3.73732 13.1635 3.55819 13.4826C3.2214 14.0826 3.05164 14.7975 3.17909 15.4773C3.31349 16.1943 3.77499 16.8195 4.56317 17.1197C5.26726 17.3877 6.20448 17.6329 7.44217 17.7788C7.47162 17.8043 7.50644 17.8336 7.54645 17.8656C7.67169 17.9658 7.85062 18.0956 8.07675 18.2248C8.52556 18.4813 9.18914 18.7513 10.0006 18.7513C10.8121 18.7513 11.4757 18.4813 11.9245 18.2248C12.1506 18.0956 12.3295 17.9658 12.4548 17.8656C12.4948 17.8336 12.5296 17.8043 12.5591 17.7788C13.7968 17.6329 14.734 17.3877 15.4381 17.1197C16.2262 16.8195 16.6877 16.1943 16.8221 15.4773C16.9496 14.7975 16.7798 14.0826 16.443 13.4826C16.2639 13.1635 16.1019 12.8473 15.9871 12.5725C15.9298 12.4355 15.8887 12.3196 15.863 12.2265C15.8503 12.1806 15.8426 12.1449 15.8382 12.1187C15.8346 12.0968 15.834 12.0859 15.834 12.0843C15.834 12.0841 15.834 12.0843 15.834 12.0843L15.834 12.0772V8.75079C15.834 5.81197 13.6608 3.38131 10.834 2.97703V2.5013ZM5.83395 8.75039C5.83395 6.4494 7.69923 4.58464 10.0006 4.58464C12.3019 4.58464 14.1673 6.44971 14.1673 8.75079V12.0846C14.1673 12.4704 14.3118 12.8865 14.4494 13.2156C14.5989 13.573 14.7948 13.9513 14.9897 14.2985C15.1792 14.636 15.2244 14.955 15.184 15.1703C15.1506 15.3484 15.0615 15.4796 14.845 15.5621C13.9493 15.9031 12.4372 16.2513 10.0006 16.2513C7.56401 16.2513 6.05196 15.9031 5.15624 15.5621C4.93969 15.4796 4.85063 15.3484 4.81722 15.1703C4.77686 14.955 4.82205 14.636 5.01153 14.2985C5.2064 13.9513 5.40231 13.573 5.5518 13.2156C5.68941 12.8865 5.83395 12.4704 5.83395 12.0846V8.75039Z"
                  fill="#344054"
                />
              </svg>
            </div>
            <div className="people-icon-box" onClick={onChangeProfile}>
              <DynamicIcon iname="user-round-check" size={18} />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="page-body" style={{ marginTop: "24px" }}>
          <Outlet />
        </div>
      </div>

      <JisPrompt
        open={isDraft}
        title="Unfinished Case filing"
        onOk={onDraftOk}
        onCancel={onDraftCancel}
      >
        <p>
          We observe you have an ongoing case filing, do you want to continue?
          Click Yes to continue, Click No to cancel.
        </p>
      </JisPrompt>
      {isExpired && (
        <JisAlert
          open={isExpired}
          title="Session Expired"
          subtitle="The current login session as expired. Login again to continue"
          actiontext="Goto Login"
          icon="close"
          onOk={onLogout}
        />
      )}
      {alert.open && (
        <JisAlert
          open={alert.open}
          icon={alert.icon}
          title={alert.title}
          subtitle={alert.subtitle}
          actiontext="Ok"
          onOk={onCloseAlert}
        />
      )}
      <TawkToChat />
    </div>
  ) : (
    <Navigate to="/login" replace />
  );
}

export default MainLayout;
